import { GetPipelineStageTargetingAutomationsResponse } from '@/api/queries/pipelines/useGetPipelineStageInfiniteTargetingAutomations';
import {
  AddStageToPipelineInput,
  type CreateAutomationConditionInput,
  CreateAutomationToTargetPipelineStageInput,
  GetPipelineQuery,
  GetPipelineStageTargetingAutomationsQuery,
  type PipelineTargetingAutomationCardFragment,
  ReorderPipelineStagePosition,
  UpdatePipelineInput,
  UpdatePipelineStageInput,
} from '@/gql/graphql';
import { DEFAULT_PIPELINE_STAGE_COLOUR } from '@payaca/constants/pipeline';
import { PAGINATION_ARG_DEFAULTS } from '@payaca/shared-isomorphic/constants/graphql-pagination';
import {
  computeStageDeletability,
  structuralReasonSet,
} from '@payaca/shared-isomorphic/pipelines/computeStageDeletability';
import { InfiniteData } from '@tanstack/react-query';
import { produce, WritableDraft } from 'immer';
import { guardInputToGraphqlResponse } from '../../utils/pipelineGuardConversions';

type DraftStages = WritableDraft<
  NonNullable<GetPipelineQuery['pipeline']>
>['stages'];

/**
 * Recomputes isDeletable for all stages, preserving any server-only reasons
 * (e.g. "contains projects") that the frontend can't determine.
 */
function recomputeStageDeletability(stages: DraftStages) {
  for (let i = 0; i < stages.length; i++) {
    const { reasons: structuralReasons } = computeStageDeletability(stages, i);

    // Keep server-provided reasons that the shared function doesn't cover
    const serverOnlyReasons = stages[i].isDeletable.reasons.filter(
      (r) => !structuralReasonSet.has(r)
    );

    const reasons = [...serverOnlyReasons, ...structuralReasons];
    stages[i].isDeletable = {
      __typename: 'ActionAvailabilityResult' as const,
      result: reasons.length === 0,
      reasons,
    };
  }
}

export function applyOptimisticAddStage(
  data: GetPipelineQuery,
  variables: AddStageToPipelineInput,
  publicId: string
): GetPipelineQuery {
  return produce(data, (draft) => {
    if (!draft?.pipeline) return;

    const stages = draft.pipeline.stages;
    const newStage = {
      __typename: 'PipelineStage' as const,
      id: publicId,
      publicId: publicId,
      title: variables.title,
      colour: DEFAULT_PIPELINE_STAGE_COLOUR,
      index: stages.length,
      isDeletable: {
        __typename: 'ActionAvailabilityResult' as const,
        result: true,
        reasons: [],
      },
      guard: null,
    };

    if (variables.position?.beforeStageId) {
      const beforeIndex = stages.findIndex(
        (s) => s.publicId === variables.position?.beforeStageId
      );
      if (beforeIndex !== -1) {
        newStage.index = beforeIndex;
        stages.splice(beforeIndex, 0, newStage);
        for (let i = beforeIndex + 1; i < stages.length; i++) {
          stages[i].index = i;
        }
        recomputeStageDeletability(stages);
        return;
      }
    }

    if (variables.position?.afterStageId) {
      const afterIndex = stages.findIndex(
        (s) => s.publicId === variables.position?.afterStageId
      );
      if (afterIndex !== -1) {
        newStage.index = afterIndex + 1;
        stages.splice(afterIndex + 1, 0, newStage);
        for (let i = afterIndex + 2; i < stages.length; i++) {
          stages[i].index = i;
        }
        recomputeStageDeletability(stages);
        return;
      }
    }

    stages.push(newStage);
    recomputeStageDeletability(stages);
  });
}

type DeletePipelineStageInput = {
  stageId: string;
};

export function applyOptimisticDeleteStage(
  data: GetPipelineQuery,
  variables: DeletePipelineStageInput
): GetPipelineQuery {
  return produce(data, (draft) => {
    if (!draft?.pipeline) return;

    const stageIndex = draft.pipeline.stages.findIndex(
      (s) => s.publicId === variables.stageId
    );

    if (stageIndex !== -1) {
      draft.pipeline.stages.splice(stageIndex, 1);

      for (let i = stageIndex; i < draft.pipeline.stages.length; i++) {
        draft.pipeline.stages[i].index = i;
      }

      recomputeStageDeletability(draft.pipeline.stages);
    }
  });
}

type ReorderMutationInput = {
  id: string;
  position: ReorderPipelineStagePosition;
};

export function applyOptimisticReorderStage(
  data: GetPipelineQuery,
  variables: ReorderMutationInput
): GetPipelineQuery {
  return produce(data, (draft) => {
    if (!draft?.pipeline?.stages) return;

    const stages = draft.pipeline.stages;
    const stageIndex = stages.findIndex((s) => s.publicId === variables.id);

    if (stageIndex === -1) return;

    // Resolve target index before splicing to avoid losing the stage
    let targetIndex = -1;
    let insertOffset = 0;

    if (variables.position.beforeStageId) {
      targetIndex = stages.findIndex(
        (s) => s.publicId === variables.position.beforeStageId
      );
    } else if (variables.position.afterStageId) {
      targetIndex = stages.findIndex(
        (s) => s.publicId === variables.position.afterStageId
      );
      insertOffset = 1;
    }

    if (targetIndex === -1) return;

    const [movedStage] = stages.splice(stageIndex, 1);

    // Adjust target index if it was after the removed stage
    const adjustedIndex =
      targetIndex > stageIndex ? targetIndex - 1 : targetIndex;

    stages.splice(adjustedIndex + insertOffset, 0, movedStage);

    for (let i = 0; i < stages.length; i++) {
      stages[i].index = i;
    }

    recomputeStageDeletability(stages);
  });
}

export function applyOptimisticUpdatePipeline(
  data: GetPipelineQuery,
  variables: UpdatePipelineInput
): GetPipelineQuery {
  return produce(data, (draft) => {
    if (!draft?.pipeline) return;

    if (variables.title != null) {
      draft.pipeline.title = variables.title;
    }
    if (variables.colour != null) {
      draft.pipeline.colour = variables.colour;
    }
  });
}

export function applyOptimisticUpdateStage(
  data: GetPipelineQuery,
  variables: UpdatePipelineStageInput
): GetPipelineQuery {
  return produce(data, (draft) => {
    if (!draft?.pipeline?.stages) return;

    const stage = draft.pipeline.stages.find(
      (s) => s.publicId === variables.id
    );

    if (!stage) return;

    if (typeof variables.title === 'string') {
      stage.title = variables.title;
    }
    if (typeof variables.colour === 'string') {
      stage.colour = variables.colour;
    }
    if ('guard' in variables) {
      stage.guard = guardInputToGraphqlResponse(variables.guard);
      recomputeStageDeletability(draft.pipeline.stages);
    }
  });
}

export type ApplyOptimisticCreateAutomationArgs = {
  variables: CreateAutomationToTargetPipelineStageInput;
  optimisticId: string;
  pipelineTitle: string;
  stageTitle: string;
};

type FragmentCondition =
  PipelineTargetingAutomationCardFragment['conditions'][number];

/**
 * Converts a CreateAutomationConditionInput into the shape returned by the
 * PipelineTargetingAutomationCard fragment, for optimistic cache updates.
 */
function conditionInputToFragment(
  input: CreateAutomationConditionInput
): FragmentCondition {
  const { type: __typename, operator, value } = input;

  if (
    'connectionApplicationStatus' in value &&
    value.connectionApplicationStatus
  ) {
    return {
      __typename: 'ConnectionApplicationStatusCondition',
      operator,
      connectionApplicationStatus: value.connectionApplicationStatus,
    };
  }
  if ('eventTaskStatus' in value && value.eventTaskStatus) {
    return {
      __typename: 'EventTaskStatusCondition',
      operator,
      eventTaskStatus: value.eventTaskStatus,
    };
  }
  if ('booleanValue' in value && value.booleanValue !== undefined) {
    return {
      __typename: 'InvoiceIsDepositCondition',
      operator,
      isDepositInvoice: value.booleanValue,
    };
  }
  if ('readableInvoiceStatus' in value && value.readableInvoiceStatus) {
    return {
      __typename: 'InvoiceStatusCondition',
      operator,
      invoiceStatus: value.readableInvoiceStatus,
    };
  }
  if ('readableProposalStatus' in value && value.readableProposalStatus) {
    return {
      __typename: 'ProposalStatusCondition',
      operator,
      proposalStatus: value.readableProposalStatus,
    };
  }
  if ('dayOfWeek' in value && value.dayOfWeek) {
    return {
      __typename: 'TriggeredDayOfWeekCondition',
      operator,
      triggeredDayOfWeek: value.dayOfWeek,
    };
  }

  // All remaining condition types use IStringMatchCondition with a string value.
  // ProjectPipelineCondition is excluded from the UI (EXCLUDED_CONDITION_TYPES)
  // and handled separately in applyOptimisticCreateAutomation, so it never
  // reaches this fallback. If that changes, this branch would need updating.
  return {
    __typename,
    operator,
    value: 'stringValue' in value ? (value.stringValue ?? '') : '',
  } as FragmentCondition;
}

// Real PipelineStage `id`s are stringified sequential integers from the server.
// We don't have one for unsaved stages, so hand out `unsaved-N` placeholders
// that are unique within the session but obviously not server ids.
let unsavedStageIdCounter = 0;
function nextUnsavedStageId(): string {
  unsavedStageIdCounter += 1;
  return `unsaved-${unsavedStageIdCounter}`;
}

function emptyPipelineStage(
  publicId: string
): NonNullable<GetPipelineStageTargetingAutomationsQuery['pipelineStage']> {
  return {
    __typename: 'PipelineStage' as const,
    id: nextUnsavedStageId(),
    publicId,
    targetingAutomations: {
      __typename: 'PaginatedAutomationsResult' as const,
      items: [],
      totalCount: 0,
      offset: 0,
      limit: PAGINATION_ARG_DEFAULTS.limit,
    },
  };
}

/**
 * Build the empty InfiniteData shell used when no targeting-automations query
 * result exists yet. Needed for newly-added stages that don't exist on the
 * server: the server returns `pipelineStage: null`, or the query hasn't
 * resolved, so there's no cache entry to optimistically mutate.
 */
export function createEmptyTargetingAutomationsInfiniteData(
  pipelineStagePublicId: string
): InfiniteData<GetPipelineStageTargetingAutomationsQuery> {
  return {
    pages: [{ pipelineStage: emptyPipelineStage(pipelineStagePublicId) }],
    pageParams: [PAGINATION_ARG_DEFAULTS.offset],
  };
}

export function applyOptimisticCreateAutomation(
  data: InfiniteData<GetPipelineStageTargetingAutomationsQuery>,
  args: ApplyOptimisticCreateAutomationArgs
): InfiniteData<GetPipelineStageTargetingAutomationsQuery> {
  const { variables, optimisticId, pipelineTitle, stageTitle } = args;

  const userConditions = (variables.conditions ?? []).map(
    conditionInputToFragment
  );

  return produce(data, (draft) => {
    const lastPageIndex = draft.pages.length - 1;
    const lastPage = draft.pages[lastPageIndex];
    if (!lastPage) return;

    // For newly-added stages the server returns `pipelineStage: null` because
    // the stage isn't persisted yet — synthesize a shell so the optimistic
    // trigger is visible until commit refetches real server data.
    if (!lastPage.pipelineStage) {
      lastPage.pipelineStage = emptyPipelineStage(variables.pipelineStageId);
    }

    const optimisticAutomation: NonNullable<GetPipelineStageTargetingAutomationsResponse>['targetingAutomations']['items'][0] =
      {
        __typename: 'Automation',
        id: optimisticId,
        privateId: optimisticId,
        title: `Move project to ${stageTitle}`,
        isActive: true,
        triggerEvent: variables.triggerEvent,
        entityType: variables.entityType,
        conditions: [
          {
            __typename: 'ProjectPipelineCondition',
            operator: 'EQUALS',
            pipeline: {
              __typename: 'Pipeline',
              title: pipelineTitle,
            },
          },
          ...userConditions,
        ],
        actions: [
          {
            __typename: 'MoveProjectToPipelineStageAutomationAction',
            targetStage: {
              __typename: 'PipelineStage',
              title: stageTitle,
            },
          },
        ],
      };

    lastPage.pipelineStage.targetingAutomations.items.push(
      optimisticAutomation
    );

    if (draft.pages[0].pipelineStage?.targetingAutomations.totalCount != null) {
      draft.pages[0].pipelineStage.targetingAutomations.totalCount += 1;
    }
  });
}

export function applyOptimisticDeleteAutomation(
  data: InfiniteData<GetPipelineStageTargetingAutomationsQuery>,
  automationId: string
): InfiniteData<GetPipelineStageTargetingAutomationsQuery> {
  return produce(data, (draft) => {
    for (const page of draft.pages) {
      if (!page.pipelineStage?.targetingAutomations) continue;

      const items = page.pipelineStage.targetingAutomations.items;
      const index = items.findIndex((a) => a.id === automationId);

      if (index !== -1) {
        items.splice(index, 1);
        break;
      }
    }

    if (draft.pages[0].pipelineStage?.targetingAutomations.totalCount != null) {
      draft.pages[0].pipelineStage.targetingAutomations.totalCount -= 1;
    }
  });
}
